// src/pages/itsm/ITSMReports.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, StatTile, MiniStat, Avatar } from '../../components/ui/UI'

type Tone = 'neutral' | 'violet' | 'success' | 'danger' | 'info' | 'warning' | 'accent'

// Resolve a semantic tone to a concrete color value (for bar fills etc,
// where a raw color is needed rather than a Pill class).
function toneColor(tone: Tone): string {
    if (tone === 'neutral') return T.text3
    if (tone === 'accent') return T.text
    return (T as any)[tone]
}

const STATUS_TONE: Record<string, Tone> = {
    open: 'info', in_progress: 'warning', pending_approval: 'violet',
    resolved: 'success', closed: 'neutral',
}
const PRIORITY_TONE: Record<string, Tone> = {
    low: 'neutral', medium: 'warning', high: 'danger', urgent: 'violet',
}

export default function ITSMReports() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'overview' | 'tickets' | 'agents' | 'assets'>('overview')
    const [data, setData] = useState<any>({
        tickets: [], agents: [], assets: [], pos: [], problems: [], changes: []
    })

    useEffect(() => { fetchAll() }, [])

    const fetchAll = async () => {
        const [
            { data: tickets },
            { data: agents },
            { data: assets },
            { data: pos },
            { data: problems },
            { data: changes },
        ] = await Promise.all([
            supabase.from('itsm_tickets').select('*, itsm_services(name), itsm_agents!assigned_agent_id(name)'),
            supabase.from('itsm_agents').select('*, itsm_team_agents(team_id)'),
            supabase.from('itsm_assets').select('*, itsm_asset_categories(name)'),
            supabase.from('itsm_purchase_orders').select('*'),
            supabase.from('itsm_problems').select('*'),
            supabase.from('itsm_changes').select('*'),
        ])
        setData({ tickets: tickets || [], agents: agents || [], assets: assets || [], pos: pos || [], problems: problems || [], changes: changes || [] })
        setLoading(false)
    }

    const { tickets, agents, assets, pos, problems, changes } = data

    // Ticket Stats
    const ticketsByStatus = ['open', 'in_progress', 'pending_approval', 'resolved', 'closed'].map(s => ({
        status: s, count: tickets.filter((t: any) => t.status === s).length
    }))

    const ticketsByPriority = ['low', 'medium', 'high', 'urgent'].map(p => ({
        priority: p, count: tickets.filter((t: any) => t.priority === p).length
    }))

    const ticketsByAgent = agents.map((a: any) => ({
        name: a.name,
        count: tickets.filter((t: any) => t.assigned_agent_id === a.id).length,
        resolved: tickets.filter((t: any) => t.assigned_agent_id === a.id && t.status === 'resolved').length,
    })).filter((a: any) => a.count > 0).sort((a: any, b: any) => b.count - a.count)

    const slaBreached = tickets.filter((t: any) => t.sla_breached).length
    const slaCompliance = tickets.length > 0 ? Math.round(((tickets.length - slaBreached) / tickets.length) * 100) : 100

    const s = styles

    if (loading) return <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>
                <ArrowLeft size={13} /> Back to ITSM
            </button>

            <div style={s.header}>
                <div>
                    <h1 style={s.title}>ITSM Reports & Analytics</h1>
                    <p style={s.subtitle}>Performance overview and insights</p>
                </div>
                <button
                    onClick={toggleTheme}
                    aria-label="Toggle dark mode"
                    style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer', flexShrink: 0 }}
                >
                    {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                </button>
            </div>

            {/* Tabs */}
            <div style={s.tabs}>
                {[
                    { key: 'overview', label: '📊 Overview' },
                    { key: 'tickets', label: '🎫 Tickets' },
                    { key: 'agents', label: '👥 Agents' },
                    { key: 'assets', label: '💻 Assets' },
                ].map(tab => (
                    <button key={tab.key}
                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? T.surface : 'transparent', color: activeTab === tab.key ? T.text : T.text2, boxShadow: activeTab === tab.key ? T.shadowSm : 'none' }}
                        onClick={() => setActiveTab(tab.key as any)}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* KPI Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                        {([
                            { label: 'Total Tickets', value: tickets.length, tone: 'info' as Tone, sub: `${tickets.filter((t: any) => t.status === 'open').length} open` },
                            { label: 'SLA Compliance', value: `${slaCompliance}%`, tone: (slaCompliance >= 90 ? 'success' : 'danger') as Tone, sub: `${slaBreached} breached` },
                            { label: 'Active Assets', value: assets.filter((a: any) => a.status === 'active').length, tone: 'violet' as Tone, sub: `of ${assets.length} total` },
                            { label: 'Open POs', value: pos.filter((p: any) => ['draft', 'pending_approval'].includes(p.status)).length, tone: 'warning' as Tone, sub: `SAR ${pos.reduce((s: number, p: any) => s + (p.total_amount || 0), 0).toLocaleString()}` },
                            { label: 'Open Problems', value: problems.filter((p: any) => p.status !== 'closed').length, tone: 'danger' as Tone, sub: `${problems.filter((p: any) => p.status === 'known_error').length} known errors` },
                            { label: 'Active Changes', value: changes.filter((c: any) => !['closed', 'cancelled'].includes(c.status)).length, tone: 'info' as Tone, sub: `${changes.filter((c: any) => c.status === 'implementing').length} implementing` },
                            { label: 'Active Agents', value: agents.filter((a: any) => a.is_active).length, tone: 'success' as Tone, sub: 'handling tickets' },
                            { label: 'Resolved Today', value: tickets.filter((t: any) => t.resolved_at && new Date(t.resolved_at).toDateString() === new Date().toDateString()).length, tone: 'success' as Tone, sub: 'tickets resolved' },
                        ]).map((kpi, i) => (
                            <Card key={i} style={{ padding: 0 }}>
                                <StatTile label={kpi.label} value={kpi.value} sub={kpi.sub} tone={kpi.tone} />
                            </Card>
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* Tickets by Status */}
                        <Card>
                            <p style={s.cardTitle}>Tickets by Status</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                                {ticketsByStatus.map(item => {
                                    const tone = STATUS_TONE[item.status] || 'info'
                                    return (
                                        <div key={item.status}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.text2, textTransform: 'capitalize' }}>{item.status.replace('_', ' ')}</span>
                                                <span className="jr-mono" style={{ fontSize: 12, fontWeight: 700, color: toneColor(tone) }}>{item.count}</span>
                                            </div>
                                            <div style={{ height: 8, background: T.surface3, borderRadius: 99, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: tickets.length > 0 ? `${(item.count / tickets.length) * 100}%` : '0%', background: toneColor(tone), borderRadius: 99, transition: 'width 0.8s ease' }} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Card>

                        {/* Tickets by Priority */}
                        <Card>
                            <p style={s.cardTitle}>Tickets by Priority</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                                {ticketsByPriority.map(item => {
                                    const tone = PRIORITY_TONE[item.priority] || 'neutral'
                                    return (
                                        <div key={item.priority}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.text2, textTransform: 'capitalize' }}>{item.priority}</span>
                                                <span className="jr-mono" style={{ fontSize: 12, fontWeight: 700, color: toneColor(tone) }}>{item.count}</span>
                                            </div>
                                            <div style={{ height: 8, background: T.surface3, borderRadius: 99, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: tickets.length > 0 ? `${(item.count / tickets.length) * 100}%` : '0%', background: toneColor(tone), borderRadius: 99 }} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12 }}>
                        {([
                            { label: 'Total', value: tickets.length, tone: 'info' as Tone },
                            { label: 'Open', value: tickets.filter((t: any) => t.status === 'open').length, tone: 'warning' as Tone },
                            { label: 'Resolved', value: tickets.filter((t: any) => t.status === 'resolved').length, tone: 'success' as Tone },
                            { label: 'SLA Breached', value: slaBreached, tone: 'danger' as Tone },
                            { label: 'Avg Resolution', value: '—', tone: 'violet' as Tone },
                            { label: 'Compliance', value: `${slaCompliance}%`, tone: (slaCompliance >= 90 ? 'success' : 'danger') as Tone },
                        ]).map((stat, i) => (
                            <MiniStat key={i} label={stat.label} value={stat.value} tone={stat.tone} />
                        ))}
                    </div>

                    <Card>
                        <p style={s.cardTitle}>Recent Tickets</p>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                    {['Ticket', 'Title', 'Service', 'Priority', 'Status', 'Agent'].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.slice(0, 10).map((t: any) => (
                                    <tr key={t.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                                        <td style={s.td}><span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{t.ticket_number}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{t.title}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{t.itsm_services?.name || '—'}</span></td>
                                        <td style={s.td}><Pill tone={PRIORITY_TONE[t.priority] || 'neutral'}>{t.priority}</Pill></td>
                                        <td style={s.td}><Pill tone={STATUS_TONE[t.status] || 'neutral'}>{t.status?.replace('_', ' ')}</Pill></td>
                                        <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{t.itsm_agents?.name || 'Unassigned'}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            )}

            {/* Agents Tab */}
            {activeTab === 'agents' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <Card>
                        <p style={s.cardTitle}>Agent Performance</p>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                    {['Agent', 'Role', 'Assigned', 'Resolved', 'Resolution Rate', 'Status'].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {agents.map((agent: any) => {
                                    const assigned = tickets.filter((t: any) => t.assigned_agent_id === agent.id).length
                                    const resolved = tickets.filter((t: any) => t.assigned_agent_id === agent.id && ['resolved', 'closed'].includes(t.status)).length
                                    const rate = assigned > 0 ? Math.round((resolved / assigned) * 100) : 0
                                    const rateTone: Tone = rate >= 70 ? 'success' : 'warning'
                                    return (
                                        <tr key={agent.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                                            <td style={s.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Avatar name={agent.name} size={28} />
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{agent.name}</span>
                                                </div>
                                            </td>
                                            <td style={s.td}><span style={{ fontSize: 11, color: T.text2, textTransform: 'capitalize' }}>{agent.role?.replace('_', ' ')}</span></td>
                                            <td style={s.td}><span className="jr-mono" style={{ fontSize: 13, fontWeight: 700, color: T.info }}>{assigned}</span></td>
                                            <td style={s.td}><span className="jr-mono" style={{ fontSize: 13, fontWeight: 700, color: T.success }}>{resolved}</span></td>
                                            <td style={s.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ flex: 1, height: 6, background: T.surface3, borderRadius: 99, overflow: 'hidden' }}>
                                                        <div style={{ height: '100%', width: `${rate}%`, background: toneColor(rateTone), borderRadius: 99 }} />
                                                    </div>
                                                    <span className="jr-mono" style={{ fontSize: 11, fontWeight: 700, color: toneColor(rateTone), minWidth: 32 }}>{rate}%</span>
                                                </div>
                                            </td>
                                            <td style={s.td}>
                                                <Pill tone={agent.is_active ? 'success' : 'neutral'}>{agent.is_active ? 'Active' : 'Inactive'}</Pill>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </Card>
                </div>
            )}

            {/* Assets Tab */}
            {activeTab === 'assets' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                        {([
                            { label: 'Total Assets', value: assets.length, tone: 'info' as Tone },
                            { label: 'Active', value: assets.filter((a: any) => a.status === 'active').length, tone: 'success' as Tone },
                            { label: 'In Maintenance', value: assets.filter((a: any) => a.status === 'maintenance').length, tone: 'warning' as Tone },
                            { label: 'Total Value', value: `SAR ${assets.reduce((s: number, a: any) => s + (a.purchase_cost || 0), 0).toLocaleString()}`, tone: 'violet' as Tone },
                        ]).map((stat, i) => (
                            <MiniStat key={i} label={stat.label} value={stat.value} tone={stat.tone} />
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* By Category */}
                        <Card>
                            <p style={s.cardTitle}>Assets by Category</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                                {Array.from(new Set(assets.map((a: any) => a.itsm_asset_categories?.name).filter(Boolean))).map((cat: any) => {
                                    const count = assets.filter((a: any) => a.itsm_asset_categories?.name === cat).length
                                    return (
                                        <div key={cat}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.text2 }}>{cat}</span>
                                                <span className="jr-mono" style={{ fontSize: 12, fontWeight: 700, color: T.info }}>{count}</span>
                                            </div>
                                            <div style={{ height: 6, background: T.surface3, borderRadius: 99, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${(count / assets.length) * 100}%`, background: T.info, borderRadius: 99 }} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Card>

                        {/* Warranty Expiry */}
                        <Card>
                            <p style={s.cardTitle}>Warranty Status</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                                {([
                                    { label: 'Valid Warranty', count: assets.filter((a: any) => a.warranty_expiry && new Date(a.warranty_expiry) > new Date()).length, tone: 'success' as Tone },
                                    { label: 'Expired Warranty', count: assets.filter((a: any) => a.warranty_expiry && new Date(a.warranty_expiry) <= new Date()).length, tone: 'danger' as Tone },
                                    { label: 'No Warranty Info', count: assets.filter((a: any) => !a.warranty_expiry).length, tone: 'neutral' as Tone },
                                ]).map(item => (
                                    <div key={item.label}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: T.text2 }}>{item.label}</span>
                                            <span className="jr-mono" style={{ fontSize: 12, fontWeight: 700, color: toneColor(item.tone) }}>{item.count}</span>
                                        </div>
                                        <div style={{ height: 6, background: T.surface3, borderRadius: 99, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: assets.length > 0 ? `${(item.count / assets.length) * 100}%` : '0%', background: toneColor(item.tone), borderRadius: 99 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    tabs: { display: 'flex', gap: 4, background: T.surface2, padding: 4, borderRadius: 10, width: 'fit-content', marginBottom: 24 },
    cardTitle: { fontSize: 13, fontWeight: 700, color: T.text },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}` },
}
